// ═════════════════════════════════════════════════════════════════════════════
//  JOB WORKSPACE — pure routing helpers (project-first nav, Task 2)
// ═════════════════════════════════════════════════════════════════════════════
// Everything DOM-dependent (hiding a tab's own project selector, running its
// shim, painting the workspace header) stays in js/admin/00-shell.js — a
// classic script tied to admin.html's own ids and to globals like sbClient
// that don't exist outside a logged-in admin session. This file holds only
// the parts that are pure functions of their arguments: the `#job/<id>/<tab>`
// hash shape (parse AND format, so the pair can be tested as a round trip)
// and resolving a project id/tab against the known-good lists. Unit-tested in
// test/unit/job-workspace-routing.test.mjs without a DOM or a session.
//
// 00-shell.js consumes these through window.Ordinex.jobWorkspace (js/core/
// index.js), the same convention every other core module already uses for
// the legacy classic-script pages — so there is exactly ONE list of which
// tabs are scoped and ONE hash-shape implementation, never a second copy
// inside 00-shell.js that could quietly drift from this one.

// The screens that stay scoped inside the workspace, in strip order.
// Schedule joined last (cleanup round, 2026-08-06) — the phase-3 design doc
// deliberately deferred it: it's a company-wide Gantt board where a task
// only OPTIONALLY tags a project (schedule_tasks.project_id), and there was
// no existing per-project selector or filtered view to shim, unlike the
// other 7 (each already had a "carry a job in" pattern — jcGoToProject —
// this task generalized). Its shim, schGoToProject (js/schedule.js), is a
// real client-side FILTER over the already-fetched task list — not a
// selector swap — because Schedule has no chooser element at all (see
// 00-shell.js's own JOB_TAB_SELECTOR_IDS, which has no 'schedule' entry for
// the same reason P&L and By Project don't).
// Production Report (2026-10-16) sits right after Production: the same reports,
// read as one printable page per item instead of a list to check. Checklist
// had a strip button, a shim and a selector since it shipped but was missing
// here, so tapping it closed the job (2026-10-16 review);
// test/unit/job-workspace-wiring.test.mjs now holds the strip and this list
// to each other.
// Overview (2026-10-31) leads the strip and is where a job opens: who runs it,
// what was filed this week, what is waiting — read-only, from data that exists.
export const JOB_WORKSPACE_TABS = ['overview', 'jobcosts', 'contract', 'pnl', 'production', 'production-report', 'by-project', 'costrecipes', 'documents', 'checklist', 'schedule'];

// `#job/<id>/<tab>` -> { id, tab } | null. Rejects a tab that isn't one of
// the workspace's own screens (e.g. `#job/<id>/prices`, a company-wide tab
// with no shim) so every caller gets one yes/no answer instead of
// re-checking membership itself.
export function parseJobHash(raw) {
  const m = /^job\/([^/]+)\/([a-z-]+)$/.exec(raw || '');
  if (!m) return null;
  const [, id, tab] = m;
  if (!JOB_WORKSPACE_TABS.includes(tab)) return null;
  return { id, tab };
}

// The inverse of parseJobHash — the ONE place the hash string is built, so
// 00-shell.js never hand-assembles it a second, possibly-drifting way (a
// template literal typo here would be caught by the round-trip test, not
// discovered live in three different call sites).
export function formatJobHash(id, tab) {
  return `job/${id}/${tab}`;
}

// A requested tab resolved to a real workspace screen — the workspace's own
// default landing tab ('overview') whenever `tab` isn't one of them (absent,
// misspelled, or a company-wide tab like 'rates' that has no shim).
export function resolveLandingTab(tab) {
  return JOB_WORKSPACE_TABS.includes(tab) ? tab : 'overview';
}

// Resolves a deep-linked (or card-clicked) project id against the known
// projects list. Returns the workspace context object on a match, or null —
// NEVER a partial/blank context — so a caller can never open the workspace
// against an id nothing resolves (a stale, deleted or mistyped id must route
// Home instead; see 00-shell.js's openJobWorkspace, which is the only caller
// allowed to run a tab's shim, and only after this returns non-null).
// job_number and status ride along so the workspace header can say WHICH job
// and WHAT STATE it is in without a second lookup. Both are optional: an org
// that does not number its jobs simply has no number to show, and a row with
// no status reads as active, the same rule every other screen applies to
// `status !== 'active'`.
export function resolveJobContext(pid, projects) {
  const proj = (projects || []).find(p => p.id === pid);
  if (!proj) return null;
  return {
    id: pid,
    name: proj.name || '',
    site_code: proj.site_code || '',
    job_number: String(proj.job_number || '').trim(),
    archived: proj.status != null && proj.status !== 'active',
  };
}
